import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { getTrialState } from "@/lib/trialUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, ScanLine, MessageSquare, Calendar, Check } from "lucide-react";

/**
 * One-time welcome modal that fires when a brand-new trial user lands on
 * the dashboard. Dismissal is persisted in localStorage keyed by the
 * user's id — the sibling doesn't track a `trial_welcome_seen` flag and
 * we don't need to bother them with one when the local storage solution
 * is identical in UX terms.
 *
 * Render rules:
 *   - Only shown when `getTrialState(user)` returns an `info`-severity
 *     state (4+ days left). After day 3 the urgent banner is enough —
 *     we don't want to spring a welcome modal on someone about to lose
 *     access.
 *   - Skipped entirely for non-trial users, family seats, admins, etc.
 *   - Skipped if the localStorage flag is already set.
 */
export default function TrialWelcomeDialog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const state = getTrialState(user);
    if (!state || state.severity !== "info") return;
    const key = `ta_trial_welcome_seen_${user.id || user._id}`;
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(key) === "1") return;
    // Tiny delay so the modal feels like part of the page entrance,
    // not a flash before the user's eyes have settled.
    const t = setTimeout(() => setOpen(true), 500);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    if (user && typeof window !== "undefined") {
      const key = `ta_trial_welcome_seen_${user.id || user._id}`;
      window.localStorage.setItem(key, "1");
    }
    setOpen(false);
  };

  // Dismiss then navigate. Marking the welcome seen as part of the deep
  // link lets the user actually try the feature on first session
  // without the modal popping back if they navigate away then home.
  const goTo = (path) => {
    dismiss();
    navigate(path);
  };

  if (!user) return null;
  const state = getTrialState(user);
  if (!state) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : dismiss())}>
      <DialogContent
        className="max-w-md bg-[#0B1220] border-white/10 text-white"
        data-testid="trial-welcome-dialog"
      >
        <DialogHeader>
          <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-sky-500 flex items-center justify-center shadow-[0_0_40px_-8px_rgba(251,191,36,0.6)] mb-2">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <DialogTitle className="font-display text-center text-2xl">
            Welcome to TA Fitness
          </DialogTitle>
          <DialogDescription className="text-white/70 text-center leading-relaxed">
            You&apos;ve got <span className="font-semibold text-amber-300">
              {state.daysLeft} days of Strength
            </span> on us — every paid feature is unlocked. Here&apos;s
            what to try first:
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-2.5 py-3">
          <FeatureRow
            icon={ScanLine}
            title="Snap your meals"
            body="Photo your plate → AI returns calories + macros in seconds."
            onClick={() => goTo("/meals")}
            testid="trial-welcome-feature-meals"
          />
          <FeatureRow
            icon={MessageSquare}
            title="Ask the AI coach anything"
            body="Unlimited Claude-powered chat for nutrition, programming, and recovery."
            onClick={() => goTo("/ai-coach")}
            testid="trial-welcome-feature-coach"
          />
          <FeatureRow
            icon={Calendar}
            title="Plan your week"
            body="Drag-and-drop workout schedule, daily check-ins, and progress tracking."
            onClick={() => goTo("/workouts")}
            testid="trial-welcome-feature-plan"
          />
        </ul>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            type="button"
            onClick={dismiss}
            data-testid="trial-welcome-dismiss-btn"
            className="w-full bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-black font-semibold"
          >
            Let&apos;s go
          </Button>
          <p className="text-[11px] text-center text-white/40">
            Trial ends {state.endsAt.toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}{" "}
            · No card needed during trial
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeatureRow({ icon: Icon, title, body, onClick, testid }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className="w-full flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.03] hover:bg-white/[0.06] hover:border-amber-400/30 p-3 text-left transition group"
      >
        <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-400/30 flex items-center justify-center">
          <Icon className="w-4 h-4 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">{title}</p>
          <p className="text-xs text-white/55 leading-snug mt-0.5">{body}</p>
        </div>
        <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-1 group-hover:translate-x-0.5 transition" />
      </button>
    </li>
  );
}
